import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { usersAPI, enrollmentsAPI, filesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CoursePaymentPlanCard } from '@/components/shared/CoursePaymentPlanCard';
import { EnrollmentPaymentFields, emptyPayment, buildPaymentPayload } from '@/components/shared/EnrollmentPaymentFields';
import {
  User,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  FileText,
  FolderOpen,
  Upload,
  Loader2,
  ArrowLeft,
  Edit,
  Save,
  X,
  Trash2,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Award,
  Printer,
  FolderPlus,
  File,
  ChevronRight,
  RefreshCw,
  Eye,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminUserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Documents state
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);

  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [gradingSubmission, setGradingSubmission] = useState(null);
  const [gradeForm, setGradeForm] = useState({ grade: '', feedback: '', status: 'graded' });

  // Certificates state
  const [certificates, setCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(false);

  // Enrollments state
  const [enrollments, setEnrollments] = useState([]);

  // Payment plan management state
  const [recordPaymentFor, setRecordPaymentFor] = useState(null); // enrollment | null
  const [recordPaymentValue, setRecordPaymentValue] = useState(emptyPayment());
  const [savingPayment, setSavingPayment] = useState(false);
  const [markingRow, setMarkingRow] = useState(null); // `${enrollmentId}_${seq}`

  const fetchUserData = useCallback(async () => {
    try {
      const response = await usersAPI.getById(userId);
      setUser(response.data);
      setEditForm(response.data);
    } catch (err) {
      toast.error('Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  const fetchFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const response = await filesAPI.getUserFolderTree(userId);
      setFolders(response.data.folders || []);
      setTotalFiles(response.data.total_files || 0);
    } catch (err) {
      console.error('Failed to fetch folders');
    } finally {
      setFoldersLoading(false);
    }
  }, [userId]);

  const initializeFolders = async () => {
    try {
      await filesAPI.initUserFolders(userId);
      toast.success('Folders initialized');
      fetchFolders();
    } catch (err) {
      toast.error('Failed to initialize folders');
    }
  };

  const fetchFolderFiles = useCallback(async (folderId) => {
    setFilesLoading(true);
    try {
      const response = await filesAPI.getUserFolderFiles(userId, folderId);
      setFiles(response.data.files || []);
    } catch (err) {
      console.error('Failed to fetch files');
    } finally {
      setFilesLoading(false);
    }
  }, [userId]);

  const fetchAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const token = localStorage.getItem('elevate360_token');
      const response = await fetch(`${API_URL}/api/users/${userId}/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch assignments');
    } finally {
      setAssignmentsLoading(false);
    }
  }, [userId]);

  const fetchCertificates = useCallback(async () => {
    setCertificatesLoading(true);
    try {
      const token = localStorage.getItem('elevate360_token');
      const response = await fetch(`${API_URL}/api/users/${userId}/certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCertificates(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch certificates');
    } finally {
      setCertificatesLoading(false);
    }
  }, [userId]);

  const fetchEnrollments = useCallback(async () => {
    try {
      const response = await enrollmentsAPI.getUserEnrollments(userId);
      setEnrollments(response.data || []);
    } catch (err) {
      console.error('Failed to fetch enrollments');
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
    fetchEnrollments();
  }, [fetchUserData, fetchEnrollments]);

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchFolders();
    } else if (activeTab === 'assignments') {
      fetchAssignments();
    } else if (activeTab === 'certificates') {
      fetchCertificates();
    }
  }, [activeTab, fetchFolders, fetchAssignments, fetchCertificates]);

  useEffect(() => {
    if (selectedFolder) {
      fetchFolderFiles(selectedFolder.folder_id);
    } else {
      setFiles([]);
    }
  }, [selectedFolder, fetchFolderFiles]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('elevate360_token');
      await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });
      toast.success('Profile updated');
      setUser(editForm);
      setIsEditing(false);
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedFolder) return;

    setUploadingFile(true);
    try {
      await filesAPI.uploadUserFile(userId, selectedFolder.folder_id, file);
      toast.success('File uploaded');
      fetchFolderFiles(selectedFolder.folder_id);
      fetchFolders(); // Refresh counts
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (docId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      const token = localStorage.getItem('elevate360_token');
      await fetch(`${API_URL}/api/users/${userId}/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      if (selectedFolder) {
        fetchFolderFiles(selectedFolder.folder_id);
      }
      fetchFolders();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleDownloadFile = async (doc) => {
    try {
      const token = localStorage.getItem('elevate360_token');
      const response = await fetch(`${API_URL}/api/users/${userId}/documents/${doc.document_id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleGradeSubmission = async () => {
    if (!gradingSubmission) return;
    try {
      await filesAPI.gradeSubmission(gradingSubmission.submission_id, {
        grade: parseInt(gradeForm.grade) || null,
        feedback: gradeForm.feedback,
        status: gradeForm.status
      });
      toast.success('Submission graded');
      setGradingSubmission(null);
      setGradeForm({ grade: '', feedback: '', status: 'graded' });
      fetchAssignments();
    } catch (err) {
      toast.error('Failed to grade submission');
    }
  };

  const handlePrintCertificate = (certificateId) => {
    window.open(`${API_URL}/api/certificates/${certificateId}/pdf`, '_blank');
  };

  const handleMarkRowPaid = async (enrollment, row) => {
    const key = `${enrollment.enrollment_id}_${row.seq}`;
    setMarkingRow(key);
    try {
      await enrollmentsAPI.markInstallmentPaid(enrollment.enrollment_id, row.seq);
      toast.success(`Instalment ${row.seq} marked paid`);
      await fetchEnrollments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark instalment paid');
    } finally {
      setMarkingRow(null);
    }
  };

  const handleRecordPayment = async () => {
    const payload = buildPaymentPayload(recordPaymentValue);
    if (!payload) {
      toast.error('Choose full payment or a monthly plan first.');
      return;
    }
    setSavingPayment(true);
    try {
      await enrollmentsAPI.recordPayment(recordPaymentFor.enrollment_id, payload);
      toast.success('Payment recorded');
      setRecordPaymentFor(null);
      setRecordPaymentValue(emptyPayment());
      await fetchEnrollments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-700',
      mentor: 'bg-blue-100 text-blue-700',
      delegate: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={colors[role] || 'bg-slate-100'}>{role?.replace('_', ' ')}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      passed: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      approved: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-700', icon: XCircle },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle },
      submitted: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      pending: { color: 'bg-slate-100 text-slate-700', icon: Clock },
      needs_revision: { color: 'bg-orange-100 text-orange-700', icon: RefreshCw },
      graded: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    };
    const cfg = config[status] || config.pending;
    return (
      <Badge className={cfg.color}>
        <cfg.icon className="w-3 h-3 mr-1" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFolderIcon = (folderType) => {
    switch (folderType) {
      case 'submissions': return '📤';
      case 'returned': return '📥';
      case 'documents': return '📁';
      default: return '📂';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!user) return null;

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'employment', label: 'Courses', icon: GraduationCap },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'assignments', label: 'Assignments', icon: FileText },
    { id: 'certificates', label: 'Certificates', icon: Award },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-user-profile">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/admin/users')} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Users
        </Button>

        {/* User Header Card */}
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Avatar */}
              <div className="w-20 h-20 bg-gradient-to-br from-[#41EAD4] to-[#0D1B2A] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-[#0D1B2A]">{user.name}</h1>
                  {getRoleBadge(user.role)}
                </div>
                <p className="text-slate-500">{user.job_title || 'Delegate'}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" /> {user.email}
                  </span>
                  {user.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {user.phone}
                    </span>
                  )}
                  {user.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {user.location}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {!isEditing ? (
                  <Button variant="outline" onClick={() => setIsEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={handleSaveProfile} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-[#0D1B2A] border-b-2 border-[#41EAD4] bg-[#41EAD4]/5'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Personal Tab */}
          {activeTab === 'personal' && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    {isEditing ? (
                      <Input
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    ) : (
                      <p className="mt-1 text-[#0D1B2A]">{user.name}</p>
                    )}
                  </div>
                  <div>
                    <Label>Email</Label>
                    <p className="mt-1 text-[#0D1B2A]">{user.email}</p>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    {isEditing ? (
                      <Input
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      />
                    ) : (
                      <p className="mt-1 text-[#0D1B2A]">{user.phone || 'Not provided'}</p>
                    )}
                  </div>
                  <div>
                    <Label>Location</Label>
                    {isEditing ? (
                      <Input
                        value={editForm.location || ''}
                        onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                      />
                    ) : (
                      <p className="mt-1 text-[#0D1B2A]">{user.location || 'Not provided'}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label>Bio / Notes</Label>
                  {isEditing ? (
                    <Textarea
                      value={editForm.bio || ''}
                      onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 text-slate-600">{user.bio || 'No bio provided'}</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <Label>Role</Label>
                    <p className="mt-1">{getRoleBadge(user.role)}</p>
                  </div>
                  <div>
                    <Label>Member Since</Label>
                    <p className="mt-1 text-[#0D1B2A]">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Courses/Employment Tab */}
          {activeTab === 'employment' && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Enrollments & Courses</CardTitle>
              </CardHeader>
              <CardContent>
                {enrollments.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No enrollments yet</p>
                ) : (
                  <div className="space-y-4">
                    {enrollments.map((enrollment) => (
                      <div key={enrollment.enrollment_id} className="space-y-3">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <div>
                            <p className="font-medium text-[#0D1B2A]">{enrollment.course_title || 'Course'}</p>
                            <p className="text-sm text-slate-500">{enrollment.cohort_name || 'Cohort'}</p>
                          </div>
                          <div className="text-right space-y-2">
                            <div className="flex items-center justify-end gap-2">
                              <Badge className={enrollment.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                                {enrollment.status}
                              </Badge>
                              {enrollment.payment_status === 'paid_in_full' && (
                                <Badge className="bg-emerald-100 text-emerald-700">Paid in full</Badge>
                              )}
                              {enrollment.payment_status === 'manual_monthly' && (
                                <Badge className="bg-sky-100 text-sky-700">
                                  Monthly plan — {(enrollment.payment_plan?.installments || []).filter(r => r.status === 'paid').length}
                                  /{enrollment.payment_plan?.num_months} paid
                                </Badge>
                              )}
                              {!enrollment.payment_status && (
                                <Button variant="outline" size="sm"
                                        onClick={() => { setRecordPaymentFor(enrollment); setRecordPaymentValue(emptyPayment()); }}
                                        data-testid={`record-payment-${enrollment.enrollment_id}`}>
                                  Record payment
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Progress: {enrollment.progress || 0}%
                            </p>
                          </div>
                        </div>
                        {enrollment.payment_plan?.type === 'manual_monthly' && (
                          <CoursePaymentPlanCard
                            enrollment={enrollment}
                            renderRowAction={(enr, row) =>
                              row.status !== 'paid' ? (
                                <Button variant="outline" size="sm"
                                        disabled={markingRow === `${enr.enrollment_id}_${row.seq}`}
                                        onClick={() => handleMarkRowPaid(enr, row)}
                                        data-testid={`mark-paid-${enr.enrollment_id}-${row.seq}`}>
                                  {markingRow === `${enr.enrollment_id}_${row.seq}` ? 'Saving…' : 'Mark paid'}
                                </Button>
                              ) : null
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Documents Tab - Enhanced */}
          {activeTab === 'documents' && (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Folder List */}
              <Card className="border-slate-100 lg:col-span-1">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Folders</CardTitle>
                    {folders.length === 0 && (
                      <Button size="sm" onClick={initializeFolders}>
                        <FolderPlus className="w-4 h-4 mr-1" />
                        Initialize
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {foldersLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
                    </div>
                  ) : folders.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FolderOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No folders yet</p>
                      <p className="text-xs mt-1">Click Initialize to create default folders</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {folders.map((folder) => (
                        <button
                          key={folder.folder_id}
                          onClick={() => setSelectedFolder(folder)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors text-left ${
                            selectedFolder?.folder_id === folder.folder_id
                              ? 'bg-[#41EAD4]/10 border border-[#41EAD4]'
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getFolderIcon(folder.folder_type)}</span>
                            <div>
                              <p className="font-medium text-sm text-[#0D1B2A]">{folder.name}</p>
                              <p className="text-xs text-slate-500">{folder.file_count} files</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Summary */}
                  {folders.length > 0 && (
                    <div className="mt-4 pt-4 border-t text-center">
                      <p className="text-sm text-slate-500">
                        Total: <span className="font-semibold text-[#0D1B2A]">{totalFiles}</span> files
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Files Panel */}
              <Card className="border-slate-100 lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {selectedFolder ? selectedFolder.name : 'Select a folder'}
                    </CardTitle>
                    {selectedFolder && (
                      <label>
                        <Button size="sm" className="bg-[#0D1B2A]" disabled={uploadingFile}>
                          {uploadingFile ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload File
                        </Button>
                        <input type="file" className="hidden" onChange={handleFileUpload} />
                      </label>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedFolder ? (
                    <div className="text-center py-12 text-slate-500">
                      <FolderOpen className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>Select a folder to view files</p>
                    </div>
                  ) : filesLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
                    </div>
                  ) : files.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <File className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No files in this folder</p>
                      <p className="text-xs mt-1">Upload files using the button above</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {files.map((doc) => (
                        <div 
                          key={doc.document_id} 
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <File className="w-5 h-5 text-slate-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium text-[#0D1B2A] truncate">{doc.filename}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span>{formatFileSize(doc.file_size)}</span>
                                <span>•</span>
                                <span>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDownloadFile(doc)}
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteFile(doc.document_id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Assignments Tab - Enhanced with Grading */}
          {activeTab === 'assignments' && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Assignments & Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                {assignmentsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
                  </div>
                ) : assignments.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No assignments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {assignments.map((assignment) => (
                      <div key={assignment.assignment_id} className="p-4 border rounded-xl">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-[#0D1B2A]">{assignment.title}</h4>
                            <p className="text-sm text-slate-500">{assignment.course_title}</p>
                          </div>
                          {getStatusBadge(assignment.status)}
                        </div>

                        {/* Submission Info */}
                        {assignment.submitted_at && (
                          <div className="mb-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Submitted:</p>
                            <p className="text-sm text-[#0D1B2A]">
                              {new Date(assignment.submitted_at).toLocaleString()}
                            </p>
                            {assignment.submission_url && (
                              <a
                                href={assignment.submission_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#41EAD4] text-sm hover:underline mt-1 block"
                              >
                                View Submission
                              </a>
                            )}
                          </div>
                        )}

                        {/* Feedback */}
                        {assignment.feedback && (
                          <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-600 mb-1 font-medium">Feedback:</p>
                            <p className="text-sm text-slate-700">{assignment.feedback}</p>
                          </div>
                        )}

                        {/* Grade Action for Submitted */}
                        {assignment.status === 'submitted' && (
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => {
                                setGradingSubmission(assignment);
                                setGradeForm({ grade: '', feedback: 'Great work!', status: 'passed' });
                              }}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Grade
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Grading Modal */}
                {gradingSubmission && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                      <h3 className="text-lg font-semibold mb-4">Grade Submission</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        {gradingSubmission.title}
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <Label>Grade (0-100)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={gradeForm.grade}
                            onChange={(e) => setGradeForm({ ...gradeForm, grade: e.target.value })}
                            placeholder="Enter grade..."
                          />
                        </div>
                        
                        <div>
                          <Label>Status</Label>
                          <div className="flex gap-2 mt-1">
                            <Button
                              size="sm"
                              variant={gradeForm.status === 'passed' ? 'default' : 'outline'}
                              onClick={() => setGradeForm({ ...gradeForm, status: 'passed' })}
                              className={gradeForm.status === 'passed' ? 'bg-emerald-600' : ''}
                            >
                              Pass
                            </Button>
                            <Button
                              size="sm"
                              variant={gradeForm.status === 'failed' ? 'default' : 'outline'}
                              onClick={() => setGradeForm({ ...gradeForm, status: 'failed' })}
                              className={gradeForm.status === 'failed' ? 'bg-red-600' : ''}
                            >
                              Fail
                            </Button>
                            <Button
                              size="sm"
                              variant={gradeForm.status === 'needs_revision' ? 'default' : 'outline'}
                              onClick={() => setGradeForm({ ...gradeForm, status: 'needs_revision' })}
                              className={gradeForm.status === 'needs_revision' ? 'bg-orange-600' : ''}
                            >
                              Needs Revision
                            </Button>
                          </div>
                        </div>
                        
                        <div>
                          <Label>Feedback</Label>
                          <Textarea
                            value={gradeForm.feedback}
                            onChange={(e) => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                            rows={3}
                            placeholder="Add feedback for the student..."
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-2 mt-6">
                        <Button variant="outline" onClick={() => setGradingSubmission(null)}>
                          Cancel
                        </Button>
                        <Button onClick={handleGradeSubmission} className="bg-[#0D1B2A]">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Submit Grade
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Certificates Tab */}
          {activeTab === 'certificates' && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Certificates</CardTitle>
              </CardHeader>
              <CardContent>
                {certificatesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
                  </div>
                ) : certificates.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Award className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No certificates earned yet</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {certificates.map((cert) => (
                      <div key={cert.certificate_id} className="p-4 border rounded-xl bg-gradient-to-br from-[#D4AF37]/10 to-white">
                        <div className="flex items-start justify-between mb-3">
                          <Award className="w-8 h-8 text-[#D4AF37]" />
                          <Badge className="bg-emerald-100 text-emerald-700">Earned</Badge>
                        </div>
                        <h4 className="font-semibold text-[#0D1B2A] mb-1">{cert.course_title}</h4>
                        <p className="text-xs text-slate-500 mb-3">
                          Issued: {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : 'N/A'}
                        </p>
                        <Button
                          size="sm"
                          className="w-full bg-[#0D1B2A]"
                          onClick={() => handlePrintCertificate(cert.certificate_id)}
                        >
                          <Printer className="w-4 h-4 mr-2" />
                          Print Certificate
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Record Payment Dialog */}
        <Dialog open={!!recordPaymentFor} onOpenChange={() => setRecordPaymentFor(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record payment — {recordPaymentFor?.course_title || 'Course'}</DialogTitle>
            </DialogHeader>
            <EnrollmentPaymentFields
              value={recordPaymentValue}
              onChange={setRecordPaymentValue}
              course={null}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRecordPaymentFor(null)}>Cancel</Button>
              <Button onClick={handleRecordPayment} disabled={savingPayment}
                      className="bg-[#0D1B2A] hover:bg-[#1B3A4B]">
                {savingPayment ? 'Saving…' : 'Record payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminUserProfilePage;
